import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError, eur, dateFmt, downloadFile } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/permissions";
import { PageHeader, EmptyState, Th, Td } from "@/components/common";
import { ListFilter, useListFilter, csvMoney } from "@/components/common/ListFilter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SearchSelect } from "@/components/SearchSelect";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useI18n } from "@/i18n";
import { toast } from "sonner";
import { Megaphone, FileText, FileSpreadsheet, Trash2, Upload } from "lucide-react";

export default function Promotions({ embedded = false }) {
  const { user } = useAuth();
  const { t } = useI18n();
  const isAdmin = user?.role === "admin";
  const canImport = can(user, "promotions:import");
  const SHOW_IMPORT = true; // Excel-Import sichtbar (Datenübernahme/erneuter Import)
  const nav = useNavigate();
  const [rows, setRows] = useState(null);
  const [summary, setSummary] = useState([]);
  const [year, setYear] = useState("alle");
  const [importOpen, setImportOpen] = useState(false);
  const [preview, setPreview] = useState(null); // parsed excel rows
  const [mapping, setMapping] = useState({}); // excel_row -> variant_id
  const [products, setProducts] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    const q = year === "alle" ? "" : `?year=${year}`;
    api.get(`/promotions${q}`).then((r) => setRows(r.data)).catch(() => setRows([]));
    api.get("/promotions/summary").then((r) => setSummary(r.data)).catch(() => {});
  }, [year]);
  useEffect(() => { load(); }, [load]);

  const del = async (id) => {
    if (!window.confirm(t("pr.cancelConfirm"))) return;
    try { await api.delete(`/promotions/${id}`); toast.success(t("common.cancelled")); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const openImport = () => {
    setImportOpen(true); setPreview(null); setMapping({});
    api.get("/inventory").then((r) => setProducts([...r.data].sort((a, b) => (a.product_name || "").localeCompare(b.product_name || "", "de") || (a.sku || "").localeCompare(b.sku || "", "de")))).catch(() => setProducts([]));
  };
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post("/promotions/import/preview", fd);
      setPreview(r.data.rows || []);
      const m = {};
      (r.data.rows || []).forEach((row) => { if (row.suggested_variant_id) m[row.excel_row] = row.suggested_variant_id; });
      setMapping(m);
      toast.success(t("common.rowsRead", { count: (r.data.rows || []).length }));
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };
  const confirmImport = async () => {
    const items = (preview || [])
      .filter((row) => mapping[row.excel_row] && mapping[row.excel_row] !== "__none__")
      .map((row) => ({ variant_id: mapping[row.excel_row], qty_2025: row.qty_2025, qty_2026: row.qty_2026 }));
    if (!items.length) { toast.error(t("common.mapAtLeastOne")); return; }
    const bookings = items.reduce((n, it) => n + (it.qty_2025 > 0 ? 1 : 0) + (it.qty_2026 > 0 ? 1 : 0), 0);
    if (!window.confirm(t("pr.importConfirm", { rows: items.length, bookings }))) return;
    setBusy(true);
    try {
      const r = await api.post("/promotions/import/confirm", { items });
      toast.success(`${t("pr.importDone", { created: r.data.created })}${r.data.skipped ? ` · ${t("pr.skippedImported", { count: r.data.skipped })}` : ""}`);
      setImportOpen(false); setPreview(null); setMapping({}); setYear("alle"); load();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  const filter = useListFilter(rows, { fields: ["product_name", "sku", "event", "created_by_email"], dateField: "occurred_at", storageKey: "promotions" });
  const view = filter.filtered;
  const total = view.reduce((a, r) => a + (r.total_cost || 0), 0);
  const totalQty = view.reduce((a, r) => a + (r.quantity || 0), 0);
  const years = summary.map((s) => s.year);

  return (
    <div className="flex h-full flex-col">
      <PageHeader title={t("pr.title")} subtitle={embedded ? "" : t("pr.subtitle")} compact={embedded}
        actions={<>
          <Button variant="outline" onClick={() => nav(`/promotion/report/${year}`)} data-testid="promo-report-btn"><FileText className="h-4 w-4 mr-1.5" /> {t("pr.pdfExport")} {year !== "alle" ? year : ""}</Button>
          <Button variant="outline" onClick={() => downloadFile(`/promotions/export.xlsx${year === "alle" ? "" : `?year=${year}`}`, `promotion_${year}.xlsx`)} data-testid="promo-excel-btn"><FileSpreadsheet className="h-4 w-4 mr-1.5" /> {t("pr.excelExport")} {year !== "alle" ? year : ""}</Button>
        </>} />

      <div className="mb-4 flex flex-wrap items-end gap-4 shrink-0">
        <div>
          <div className="text-xs text-muted-foreground mb-1">{t("common.fiscalYear")}</div>
          <Select value={year} onValueChange={setYear}>
            <SelectTrigger className="w-40" data-testid="promo-year"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="alle">{t("common.allYears")}</SelectItem>
              {years.map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {SHOW_IMPORT && canImport && <div>
          <div className="text-xs text-muted-foreground mb-1">{t("pr.dataMaintenance")}</div>
          <Button variant="ghost" size="sm" onClick={openImport} data-testid="promo-import-btn"><Upload className="h-4 w-4 mr-1.5" /> {t("pr.importQty")}</Button>
        </div>}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4 shrink-0">
        <div className="rounded-md border border-border bg-card p-4 shadow-sm" data-testid="promo-total">
          <div className="text-xs uppercase tracking-wider text-muted-foreground">{t("ow.costTotal")}</div>
          <div className="font-mono text-2xl font-bold">{eur(total)}</div>
        </div>
        <div className="rounded-md border border-border bg-card p-4 shadow-sm">
          <div className="text-xs uppercase tracking-wider text-muted-foreground">{t("common.pcsTotal")}</div>
          <div className="font-mono text-2xl font-bold text-primary">{totalQty}</div>
        </div>
      </div>

      <ListFilter filter={filter} testId="promo" withDates placeholder={t("filter.searchProductSku")}
        exportName="promotion" exportColumns={[
          { label: t("common.date"), value: (r) => dateFmt(r.occurred_at) },
          { label: t("common.product"), value: (r) => r.product_name },
          { label: "SKU", value: (r) => r.sku },
          { label: t("common.quantity"), value: (r) => r.quantity },
          { label: t("ow.costPerUnit"), value: (r) => csvMoney(r.unit_cost) },
          { label: t("ow.costSum"), value: (r) => csvMoney(r.total_cost) },
          { label: t("pr.eventPurpose"), value: (r) => r.event || "" },
          { label: t("common.source"), value: (r) => (r.source === "import" ? t("pr.sourceImport") : t("pr.sourceManual")) },
          { label: t("common.person"), value: (r) => r.created_by_email || "" },
        ]} />

      {rows === null ? <div className="text-muted-foreground">{t("common.loading")}</div> :
        view.length === 0 ? <EmptyState testId="promo-empty" icon={Megaphone} title={t("pr.empty")} hint={t("pr.emptyHint")} /> : (
          <div className="flex-1 min-h-0 overflow-auto rounded-md border border-border bg-card shadow-sm">
            <table className="w-full">
              <thead className="bg-muted/50"><tr>
                <Th sticky>{t("common.date")}</Th><Th sticky>{t("common.product")} / SKU</Th><Th sticky right>{t("common.quantity")}</Th>
                <Th sticky right>{t("ow.costPerUnit")}</Th><Th sticky right>{t("ow.costSum")}</Th>
                <Th sticky>{t("pr.eventPurpose")}</Th><Th sticky>{t("common.source")}</Th><Th sticky>{t("common.person")}</Th><Th sticky></Th>
              </tr></thead>
              <tbody data-testid="promo-table">
                {view.map((r) => (
                  <tr key={r.id} className="hover:bg-muted/40 transition-colors">
                    <Td className="whitespace-nowrap">{dateFmt(r.occurred_at)}</Td>
                    <Td><div className="font-medium">{r.product_name}</div><div className="text-xs text-muted-foreground font-mono">{r.sku}</div></Td>
                    <Td right mono>{r.quantity}</Td>
                    <Td right mono>{eur(r.unit_cost)}</Td>
                    <Td right mono className="font-semibold">{eur(r.total_cost)}</Td>
                    <Td className="text-xs max-w-[240px] truncate" title={r.event}>{r.event || "—"}</Td>
                    <Td><span className={`inline-flex rounded-full px-2 py-0.5 text-xs ${r.source === "import" ? "bg-muted text-muted-foreground" : "bg-primary/15 text-primary"}`}>{r.source === "import" ? t("pr.sourceImport") : t("pr.sourceManual")}</span></Td>
                    <Td className="text-xs">{r.created_by_email || "—"}</Td>
                    <Td right>{isAdmin && <Button size="sm" variant="ghost" onClick={() => del(r.id)} data-testid={`promo-del-${r.id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

      <Dialog open={importOpen} onOpenChange={(o) => !o && setImportOpen(false)}>
        <DialogContent className="max-w-4xl">
          <DialogHeader><DialogTitle>{t("pr.importTitle")}</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground -mt-1">
            {t("pr.importHint")}
          </p>
          {!preview ? (
            <div className="py-6">
              <Input type="file" accept=".xlsx" onChange={onFile} disabled={busy} data-testid="promo-import-file" />
              {busy && <p className="text-sm text-muted-foreground mt-2">{t("common.readingFile")}</p>}
            </div>
          ) : (
            <div className="max-h-[60vh] overflow-auto rounded-md border border-border">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 sticky top-0"><tr>
                  <Th>{t("common.excelRow")}</Th><Th right>2025 (AJ)</Th><Th right>2026 (AL)</Th><Th>{t("common.mapProduct")}</Th>
                </tr></thead>
                <tbody data-testid="promo-import-preview">
                  {preview.map((row) => (
                    <tr key={row.excel_row} className={mapping[row.excel_row] ? "" : "bg-destructive/5"}>
                      <Td><div className="max-w-[260px] truncate" title={row.excel_name}>{row.excel_name}</div>{row.ean && <div className="text-xs text-muted-foreground font-mono">EAN {row.ean}</div>}</Td>
                      <Td right mono>{row.qty_2025 || "—"}</Td>
                      <Td right mono>{row.qty_2026 || "—"}</Td>
                      <Td>
                        <SearchSelect
                          testId={`promo-map-${row.excel_row}`}
                          className="w-72"
                          value={mapping[row.excel_row] || "__none__"}
                          onChange={(v) => setMapping({ ...mapping, [row.excel_row]: v })}
                          placeholder={t("common.dontImport")}
                          options={[{ value: "__none__", label: t("common.dontImport") }, ...products.map((p) => ({ value: p.variant_id, label: `${p.product_name} · ${p.sku}` }))]}
                        />
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <DialogFooter>
            {preview && <Button variant="outline" onClick={() => { setPreview(null); setMapping({}); }} data-testid="promo-import-reset">{t("common.otherFile")}</Button>}
            {preview && <Button onClick={confirmImport} disabled={busy} data-testid="promo-import-confirm">{t("common.confirmImport")}</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
